import { Injectable } from '@angular/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { environment } from 'environments/environment';

export interface ApiLoggingConfig {
  enabled: boolean;
  retentionDays: number;
  maxTotalBytes: number;
  maxLineBytes: number;
  directory: Directory;
  filePrefix: string;
}

export interface ApiLogEntry {
  ts: string; // ISO
  requestId: string;
  method: string;
  url: string;
  status: number | null;
  ok: boolean;
  durationMs: number;
  error?: {
    message?: string;
    bodySnippet?: string;
  };
}

const LS_KEYS = {
  enabled: 'pb.customer.apiLoggingEnabled',
  retentionDays: 'pb.customer.apiLoggingRetentionDays',
  maxTotalBytes: 'pb.customer.apiLoggingMaxTotalBytes',
  maxLineBytes: 'pb.customer.apiLoggingMaxLineBytes',
};

@Injectable({ providedIn: 'root' })
export class ApiLogStoreService {
  private writeChain: Promise<void> = Promise.resolve();

  init(): Promise<void> {
    return this.enqueue(async () => {
      await this.pruneInternal(this.getEffectiveConfig());
    });
  }

  append(entry: ApiLogEntry): Promise<void> {
    return this.enqueue(async () => {
      const cfg = this.getEffectiveConfig();
      if (!cfg.enabled) return;

      await this.pruneInternal(cfg);

      const fileName = this.getDailyFileName(cfg.filePrefix, new Date());
      const jsonLine = this.toCappedJsonLine(entry, cfg.maxLineBytes);

      await Filesystem.appendFile({
        directory: cfg.directory,
        path: fileName,
        data: jsonLine,
        encoding: Encoding.UTF8,
      });
    });
  }

  async exportCombinedLogFile(): Promise<{ name: string; uri: string } | null> {
    const cfg = this.getEffectiveConfig();
    if (!cfg.enabled) return null;

    return this.enqueue(async () => {
      const files = await this.listLogFiles(cfg);
      if (!files.length) return null;

      const combined = await this.readFilesAsText(cfg, files);
      const exportName = `${cfg.filePrefix}-export-${this.formatTimestampForFile(new Date())}.jsonl`;

      await Filesystem.writeFile({
        directory: cfg.directory,
        path: exportName,
        data: combined,
        encoding: Encoding.UTF8,
        recursive: false,
      });

      const uri = await Filesystem.getUri({ directory: cfg.directory, path: exportName });
      return { name: exportName, uri: uri.uri };
    });
  }

  async readAllLogsText(): Promise<string> {
    const cfg = this.getEffectiveConfig();

    return this.enqueue(async () => {
      const files = await this.listLogFiles(cfg);
      return this.readFilesAsText(cfg, files);
    });
  }

  setEnabledOverride(enabled: boolean | null): void {
    try {
      if (enabled === null) {
        localStorage.removeItem(LS_KEYS.enabled);
      } else {
        localStorage.setItem(LS_KEYS.enabled, enabled ? 'true' : 'false');
      }
    } catch {
      // ignore
    }
  }

  getEnabledEffective(): boolean {
    return this.getEffectiveConfig().enabled;
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Config + queue
  // ────────────────────────────────────────────────────────────────────────────

  private enqueue<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.writeChain.then(fn, fn);
    this.writeChain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  private getEffectiveConfig(): ApiLoggingConfig {
    const base = (environment as any)?.apiLogging ?? {};

    const enabled =
      this.readBool(localStorage.getItem(LS_KEYS.enabled)) ??
      this.readBool(base.enabled) ??
      false;

    const retentionDays =
      this.readInt(localStorage.getItem(LS_KEYS.retentionDays)) ??
      this.readInt(base.retentionDays) ??
      10;

    const maxTotalBytes =
      this.readInt(localStorage.getItem(LS_KEYS.maxTotalBytes)) ??
      this.readInt(base.maxTotalBytes) ??
      5 * 1024 * 1024;

    const maxLineBytes =
      this.readInt(localStorage.getItem(LS_KEYS.maxLineBytes)) ??
      this.readInt(base.maxLineBytes) ??
      4096;

    const directory = this.resolveDirectory(base.directory);
    const filePrefix = String(base.filePrefix ?? 'api-log');

    return {
      enabled,
      retentionDays: this.clampInt(retentionDays, 1, 365),
      maxTotalBytes: this.clampInt(maxTotalBytes, 0, 1024 * 1024 * 1024),
      maxLineBytes: this.clampInt(maxLineBytes, 256, 256 * 1024),
      directory,
      filePrefix,
    };
  }

  private resolveDirectory(raw: unknown): Directory {
    if (raw === Directory.Cache) return Directory.Cache;
    if (raw === Directory.Data) return Directory.Data;
    if (typeof raw === 'string') {
      const v = (Directory as any)[raw];
      if (v) return v as Directory;
    }
    return Directory.Cache;
  }

  private readBool(raw: unknown): boolean | null {
    if (typeof raw === 'boolean') return raw;
    if (typeof raw !== 'string') return null;
    const v = raw.trim().toLowerCase();
    if (v === '1' || v === 'true' || v === 'yes') return true;
    if (v === '0' || v === 'false' || v === 'no') return false;
    return null;
  }

  private readInt(raw: unknown): number | null {
    if (typeof raw === 'number' && Number.isFinite(raw)) return Math.floor(raw);
    if (typeof raw !== 'string') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? Math.floor(n) : null;
  }

  private clampInt(v: number, min: number, max: number): number {
    if (!Number.isFinite(v)) return min;
    return Math.min(max, Math.max(min, Math.floor(v)));
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Prune / retention
  // ────────────────────────────────────────────────────────────────────────────

  private async pruneInternal(cfg: ApiLoggingConfig): Promise<void> {
    const files = await this.listLogFiles(cfg);
    if (!files.length) return;

    const cutoff = this.startOfDay(this.addDays(new Date(), -cfg.retentionDays));

    const keep: LogFileInfo[] = [];
    for (const f of files) {
      if (f.date && f.date < cutoff) {
        await this.safeDelete(cfg, f.name);
      } else {
        keep.push(f);
      }
    }

    if (cfg.maxTotalBytes <= 0) {
      for (const f of keep) {
        await this.safeDelete(cfg, f.name);
      }
      return;
    }

    const withSizes: Array<LogFileInfo & { size: number }> = [];
    for (const f of keep) {
      const size = await this.safeSize(cfg, f.name);
      withSizes.push({ ...f, size });
    }

    withSizes.sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0));

    let total = withSizes.reduce((sum, f) => sum + f.size, 0);
    for (const f of withSizes) {
      if (total <= cfg.maxTotalBytes) break;
      await this.safeDelete(cfg, f.name);
      total -= f.size;
    }
  }

  private async listLogFiles(cfg: ApiLoggingConfig): Promise<LogFileInfo[]> {
    try {
      const res = await Filesystem.readdir({ directory: cfg.directory, path: '' });
      const names = (res.files as any[]).map((x) => (typeof x === 'string' ? x : x?.name)).filter(Boolean);

      const out: LogFileInfo[] = [];
      for (const name of names) {
        const info = this.parseLogFileName(cfg.filePrefix, String(name));
        if (info) out.push(info);
      }

      out.sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0));
      return out;
    } catch {
      return [];
    }
  }

  private async safeDelete(cfg: ApiLoggingConfig, path: string): Promise<void> {
    try {
      await Filesystem.deleteFile({ directory: cfg.directory, path });
    } catch {
      // ignore
    }
  }

  private async safeSize(cfg: ApiLoggingConfig, path: string): Promise<number> {
    try {
      const s = await Filesystem.stat({ directory: cfg.directory, path });
      return typeof (s as any).size === 'number' ? (s as any).size : 0;
    } catch {
      return 0;
    }
  }

  private async readFilesAsText(cfg: ApiLoggingConfig, files: LogFileInfo[]): Promise<string> {
    let out = '';
    for (const f of files) {
      try {
        const r = await Filesystem.readFile({ directory: cfg.directory, path: f.name, encoding: Encoding.UTF8 });
        const txt = (r.data as string) || '';
        if (txt) {
          out += txt;
          if (!txt.endsWith('\n')) out += '\n';
        }
      } catch {
        // ignore
      }
    }
    return out;
  }

  // ────────────────────────────────────────────────────────────────────────────
  // File naming + JSONL line sizing
  // ────────────────────────────────────────────────────────────────────────────

  private getDailyFileName(prefix: string, date: Date): string {
    const d = this.formatDate(date);
    return `${prefix}-${d}.jsonl`;
  }

  private parseLogFileName(prefix: string, name: string): LogFileInfo | null {
    const re = new RegExp(`^${this.escapeRegExp(prefix)}-(\\d{4})-(\\d{2})-(\\d{2})\\.jsonl$`);
    const m = name.match(re);
    if (!m) return null;

    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;

    const date = new Date(y, mo - 1, d);
    if (!Number.isFinite(date.getTime())) return null;

    return { name, date };
  }

  private toCappedJsonLine(entry: ApiLogEntry, maxLineBytes: number): string {
    const json = JSON.stringify(entry);
    const capped = this.truncateUtf8ToBytes(json, maxLineBytes);
    return capped + '\n';
  }

  private truncateUtf8ToBytes(input: string, maxBytes: number): string {
    if (maxBytes <= 0) return '';

    const enc = (globalThis as any).TextEncoder ? new TextEncoder() : null;
    if (!enc) {
      return input.length > maxBytes ? input.slice(0, maxBytes) : input;
    }

    const bytes = enc.encode(input);
    if (bytes.length <= maxBytes) return input;

    let lo = 0;
    let hi = input.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      const b = enc.encode(input.slice(0, mid));
      if (b.length <= maxBytes) lo = mid;
      else hi = mid - 1;
    }

    return input.slice(0, lo);
  }

  private formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private formatTimestampForFile(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    return `${y}${m}${d}-${hh}${mm}${ss}`;
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private addDays(date: Date, days: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  private escapeRegExp(input: string): string {
    return input.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&');
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Redaction helpers (used by interceptors)
  // ────────────────────────────────────────────────────────────────────────────

  redactUrl(url: string): string {
    try {
      const base = (environment as any)?.appUrl || (typeof window !== 'undefined' ? window.location.origin : 'https://local');
      const u = new URL(url, base);
      return u.pathname;
    } catch {
      return String(url).split(/[?#]/)[0];
    }
  }

  redactErrorBody(input: unknown): string | undefined {
    const s = this.toSafeString(input);
    if (!s) return undefined;

    const masked = s
      .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[REDACTED_EMAIL]')
      .replace(/\b([A-Za-z0-9-_]{24,})\b/g, '[REDACTED_TOKEN]');

    return masked;
  }

  private toSafeString(input: unknown): string {
    if (input == null) return '';
    if (typeof input === 'string') return input;

    try {
      if (typeof (input as any).message === 'string' && typeof (input as any).name === 'string') {
        return `${(input as any).name}: ${(input as any).message}`;
      }
    } catch {}

    try {
      return JSON.stringify(this.redactObject(input), null, 0);
    } catch {
      try {
        return String(input);
      } catch {
        return '';
      }
    }
  }

  private redactObject(input: unknown, depth = 0): unknown {
    if (depth > 4) return '[REDACTED_DEPTH]';

    if (input == null) return input;
    if (typeof input === 'string') return input;
    if (typeof input === 'number' || typeof input === 'boolean') return input;

    if (Array.isArray(input)) {
      return input.slice(0, 50).map((v) => this.redactObject(v, depth + 1));
    }

    if (typeof input === 'object') {
      const o = input as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(o)) {
        const key = k.toLowerCase();
        if (
          key.includes('password') ||
          key.includes('token') ||
          key.includes('secret') ||
          key.includes('authorization') ||
          key.includes('cookie') ||
          key.includes('email') ||
          key.includes('phone') ||
          key.includes('address') ||
          key.includes('ssn')
        ) {
          out[k] = '[REDACTED]';
          continue;
        }
        out[k] = this.redactObject(o[k], depth + 1);
      }
      return out;
    }

    return String(input);
  }
}

interface LogFileInfo {
  name: string;
  date: Date | null;
}
